/**
 * VerifyOtpModal — Phase 7C. Reusable OTP-confirmation modal used to
 * gate sensitive profile changes (phone/email/pickup permission).
 *
 * Flow:
 *   1. We auto-send an OTP to the user's primary identifier (email)
 *      using the existing /auth/login-otp/send endpoint on mount.
 *   2. User types the 6-digit code; we never verify here — the parent
 *      component re-posts the PATCH /auth/me with `_otp_code` in the
 *      body and the BACKEND validates the code as part of the same
 *      atomic write. That's the only way to make sure the OTP and the
 *      change are tied together (no separate verify→save race).
 *   3. ``onConfirmed(code)`` callback is what the parent passes —
 *      typically just `await api.patch('/auth/me', {...patch, _otp_code: code})`.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, X, ArrowsClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function VerifyOtpModal({
  identifier,
  channel = "email",
  title = "Confirm with OTP",
  body = "We've sent a 6-digit code to your email. Enter it to confirm.",
  onCancel,
  onConfirmed,
}) {
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [masked, setMasked] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // Fire the initial OTP send on mount. Re-sends are gated by a 30 s
  // cooldown so users can't spam the dispatch.
  const sendOtp = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/login-otp/send", {
        identifier, channel,
      });
      setMasked(data.masked || identifier);
      setCooldown(30);
    } catch {
      toast.error("Could not send OTP. Try again in a minute.");
    } finally { setSending(false); }
  };

  useEffect(() => { sendOtp(); /* eslint-disable-next-line */ }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(code)) {
      toast.error("Enter the 6-digit code from your email."); return;
    }
    setSubmitting(true);
    try {
      await onConfirmed(code);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP didn't match.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onCancel}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl border border-border p-6 space-y-4"
        data-testid="otp-modal">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-mustard/30 flex items-center justify-center text-mustard-deep">
            <ShieldCheck size={20} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold">{title}</h3>
            <p className="text-sm text-ink-soft mt-1">{body}</p>
            {masked && <p className="text-xs text-ink-soft mt-1">Sent to <b>{masked}</b></p>}
          </div>
          <button type="button" onClick={onCancel} className="text-ink-soft hover:text-ink">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div>
          <label className="text-xs text-ink-soft block mb-1">6-digit code</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full h-12 px-4 rounded-xl border border-border bg-white text-center text-2xl font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-terracotta"
            placeholder="••••••"
            data-testid="otp-code-input"
            autoFocus
          />
        </div>
        <div className="flex items-center justify-between text-xs">
          <button type="button" onClick={sendOtp}
            disabled={sending || cooldown > 0}
            className="text-terracotta font-semibold disabled:text-ink-soft flex items-center gap-1"
            data-testid="otp-resend">
            <ArrowsClockwise size={12} weight="bold" />
            {cooldown > 0 ? `Resend in ${cooldown}s` : sending ? "Sending…" : "Resend code"}
          </button>
          <span className="text-ink-soft">Didn't get it? Check spam.</span>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="flex-1 h-11 rounded-full border border-border">
            Cancel
          </button>
          <button disabled={submitting || code.length < 4}
            className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-50"
            data-testid="otp-confirm-btn">
            {submitting ? "Verifying…" : "Confirm & save"}
          </button>
        </div>
      </form>
    </div>
  );
}
